// src/profile/types.ts
//
// PUBLIC profile model — the subset of user data that's visible to
// other users via /u/{username} or /u/{uid}. Stored on the same
// /users/{uid} doc as the existing private profile fields, but
// readable by all authenticated users (security rules differentiate).
//
// Why a separate module instead of extending UserProfile in the
// auth store: the auth store is large, deeply tested, and currently
// owns the *private* profile (travelProfile, GDPR consent, prefs).
// The public profile has different access patterns (read-by-others,
// rate-limited writes) and different validation. Keeping concerns
// separate makes both easier to evolve.
//
// Persistence: fields below live at /users/{uid} alongside the
// private fields. Rules allow read by any authenticated user for
// the public fields and write only by the owner.
//
// Username reservation: separate collection /usernames/{lower}
// owns the uniqueness invariant. See claimUsername().

import { Timestamp } from 'firebase/firestore';

export interface PublicProfileFields {
  /** Username with @ stripped, stored as-typed (case preserved).
   *  Lookups go through username_lower (server-stored) which
   *  matches /usernames/{username_lower} reservation docs. */
  username?: string;
  /** Lower-cased copy maintained server-side for case-insensitive
   *  lookup. Always equals username.toLowerCase(). */
  username_lower?: string;
  /** Last time username was changed. Used to enforce rate limit. */
  username_changed_at?: Timestamp;
  /** Display name shown next to avatar. Sourced from auth profile
   *  on first profile-doc create; user can edit independently. */
  display_name?: string;
  /** Avatar photo URL. Mirrors auth.currentUser.photoURL by default;
   *  user can override later (out of scope for this round). */
  photo_url?: string;
  /** Account privacy. When true, follow attempts create a request
   *  that the target must approve; the item grid is hidden from
   *  non-followers. New accounts default to FALSE (public). */
  is_account_private?: boolean;
  /** Denormalized counters maintained by Cloud Functions on
   *  follow-edge changes. Authoritative source is the subcollection
   *  contents; these exist for fast profile-page render. */
  followers_count?: number;
  following_count?: number;
  posts_count?: number;
}

/** Username constraints. Locked here so client + rules + Cloud
 *  Functions all agree. */
export const USERNAME_MIN = 3;
export const USERNAME_MAX = 30;
export const USERNAME_REGEX = /^[a-z][a-z0-9_]{2,29}$/;
export const USERNAME_CHANGE_COOLDOWN_DAYS = 30;

/** Public-facing validation result. */
export type UsernameValidation =
  | { ok: true; normalized: string }
  | { ok: false; reason: 'too_short' | 'too_long' | 'invalid_chars' | 'starts_with_digit' };

export function validateUsername(input: string): UsernameValidation {
  const lower = input.trim().toLowerCase();
  if (lower.length < USERNAME_MIN) return { ok: false, reason: 'too_short' };
  if (lower.length > USERNAME_MAX) return { ok: false, reason: 'too_long' };
  // Detailed reason for the regex failure for nicer error messages.
  if (/^[0-9]/.test(lower)) return { ok: false, reason: 'starts_with_digit' };
  if (!USERNAME_REGEX.test(lower)) return { ok: false, reason: 'invalid_chars' };
  return { ok: true, normalized: lower };
}

/** Follow relationship state from the viewer's perspective. */
export type FollowState =
  | 'self'                  // viewer is the profile owner
  | 'following'             // viewer follows the profile
  | 'requested'             // viewer has a pending follow request
  | 'not_following'         // viewer doesn't follow (and no request pending)
  | 'unknown';              // still loading
